import {
  ComponentRef,
  DestroyRef,
  Directive,
  ElementRef,
  TemplateRef,
  ViewContainerRef,
  booleanAttribute,
  inject,
  input,
  numberAttribute,
} from '@angular/core';
import {
  FlexibleConnectedPositionStrategy,
  Overlay,
  OverlayRef,
} from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TooltipPanelComponent } from './tooltip-panel.component';
import {
  TooltipPosition,
  getTooltipPositions,
  resolveTooltipPosition,
} from './tooltip.positions';

@Directive({
  selector: '[interfaceTooltip]',
  host: {
    '(mouseenter)': 'show()',
    '(focusin)': 'show()',
    '(mouseleave)': 'hide()',
    '(focusout)': 'hide()',
  },
})
export class TooltipDirective {
  private readonly el = inject(ElementRef, { self: true });
  private readonly viewContainerRef = inject(ViewContainerRef, { self: true });
  private readonly overlay = inject(Overlay);
  private readonly destroyRef = inject(DestroyRef);

  readonly interfaceTooltip = input<string | TemplateRef<unknown> | null>(null);
  readonly interfaceTooltipPosition = input<TooltipPosition>('top');
  readonly interfaceTooltipDisabled = input(false, {
    transform: booleanAttribute,
  });
  readonly interfaceTooltipMaxWidth = input(256, {
    transform: numberAttribute,
  });

  private overlayRef: OverlayRef | null = null;
  private panelRef: ComponentRef<TooltipPanelComponent> | null = null;

  constructor() {
    this.destroyRef.onDestroy(() => this.dispose());
  }

  /**
   * Opens the tooltip overlay when content is present and the tip is enabled.
   */
  show(): void {
    if (this.interfaceTooltipDisabled()) {
      return;
    }

    const content = this.interfaceTooltip();
    if (content == null || content === '') {
      return;
    }

    if (!this.overlayRef) {
      this.createOverlay();
    } else {
      this.updatePositions();
    }

    if (!this.overlayRef?.hasAttached()) {
      this.attachPanel();
    }

    this.updatePanelContent(content);
    this.panelRef?.setInput('position', this.interfaceTooltipPosition());
    this.panelRef?.setInput('maxWidth', this.interfaceTooltipMaxWidth());
  }

  /**
   * Detaches the tooltip overlay without disposing it for reuse.
   */
  hide(): void {
    this.overlayRef?.detach();
    this.panelRef = null;
  }

  private createOverlay(): void {
    const positionStrategy = this.buildPositionStrategy();
    this.overlayRef = this.overlay.create({
      scrollStrategy: this.overlay.scrollStrategies.close(),
      positionStrategy,
    });

    positionStrategy.positionChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((change) => {
        this.panelRef?.setInput(
          'position',
          resolveTooltipPosition(change.connectionPair),
        );
      });
  }

  private updatePositions(): void {
    const strategy = this.overlayRef?.getConfig().positionStrategy as
      | FlexibleConnectedPositionStrategy
      | undefined;
    strategy?.withPositions(
      getTooltipPositions(this.interfaceTooltipPosition()),
    );
  }

  private buildPositionStrategy() {
    return this.overlay
      .position()
      .flexibleConnectedTo(this.el)
      .withFlexibleDimensions(false)
      .withPush(false)
      .withPositions(getTooltipPositions(this.interfaceTooltipPosition()));
  }

  private attachPanel(): void {
    if (!this.overlayRef) {
      return;
    }

    const portal = new ComponentPortal(
      TooltipPanelComponent,
      this.viewContainerRef,
    );
    this.panelRef = this.overlayRef.attach(portal);
  }

  private updatePanelContent(content: string | TemplateRef<unknown>): void {
    if (!this.panelRef) {
      return;
    }

    if (content instanceof TemplateRef) {
      this.panelRef.setInput('content', null);
      this.panelRef.setInput('contentTemplate', content);
    } else {
      this.panelRef.setInput('contentTemplate', null);
      this.panelRef.setInput('content', content);
    }
  }

  private dispose(): void {
    this.overlayRef?.dispose();
    this.overlayRef = null;
    this.panelRef = null;
  }
}
